// @ts-nocheck: converted from JS

import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { _isNil, _size } from '@/littledash';
import { type ShowStateReducer } from './Workflow.model';
import { updateQueryParams } from '@/support/RouteService.util.ts';

export const assembleWorkflowFilterOptions = ({ study, collections }: { study: Study; collections: Array<Cage> }) => {
  const { study_groups: studyGroups } = study;
  const calculations = study.settings.calculations;

  const studyGroupIds = studyGroups.map(({ name, id }) => ({
    name,
    value: id,
  }));
  const cageIds = collections.map(({ name, id }) => ({
    name,
    value: id,
  }));
  const variableFilters = calculations.map(({ name, id }) => ({
    name,
    value: id,
    type: 'latestMeasurements',
    operations: [
      {
        name: 'greater than',
        value: filterType.gt,
      },
      {
        name: 'less than',
        value: filterType.lt,
      },
      {
        name: filterType.IS_NULL,
        value: filterType.IS_NULL,
      },
      {
        name: filterType.IS_NOT_NULL,
        value: filterType.IS_NOT_NULL,
      },
    ],
  }));

  let filters = {};
  if (_size(cageIds)) {
    filters = [
      {
        value: 'cage',
        name: 'Cage Name',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: cageIds,
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: cageIds,
          },
        ],
      },
      {
        value: 'deceased',
        name: 'Living status',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: [{ value: 'null', name: 'Alive' }],
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: [{ value: 'null', name: 'Alive' }],
          },
        ],
      },
      {
        value: 'sex',
        name: 'Sex',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: [
              { value: 'm', name: 'Male' },
              { value: 'f', name: 'Female' },
              { value: 'u', name: 'Unknown' },
            ],
          },
        ],
      },
      {
        value: 'alerts',
        name: 'Alert',
        operations: [
          {
            name: 'is',
            value: filterType.eq,
            options: [
              { value: 'alert_unresolved', name: 'Unresolved' },
              { value: 'alert_resolved', name: 'Resolved' },
            ],
          },
        ],
      },
      ...variableFilters,
    ];
    if (studyGroups.length) {
      filters.push({
        value: 'study_group',
        name: 'Study Group',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: studyGroupIds,
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: studyGroupIds,
          },
        ],
      });
    }
  }
  return filters;
};

export const getNextAnimal = (subjectId, subjects, direction = 'desc') => {
  let nextAnimalIndex;

  const currentIndex = subjects.findIndex((subject) => subject.id === subjectId);

  if (direction === 'desc') {
    nextAnimalIndex = currentIndex + 1 === subjects.length ? 0 : currentIndex + 1;
  } else {
    nextAnimalIndex = currentIndex === 0 ? subjects.length - 1 : currentIndex - 1;
  }

  return subjects[nextAnimalIndex];
};

export const getFirstAnimal = (cageId, subjects) => {
  const subjectsInCage = subjects.filter((subject) => subject.cage_id === cageId);
  return subjectsInCage[0];
};

export const getNextNonEmptyCage = (cageId, collections, availableSubjects, direction = 'desc') => {
  const currentIndex = collections.findIndex((cage) => cage.id === cageId);

  if (currentIndex === -1) {
    // Fallback to first cage if current cage not found
    return collections[0];
  }

  const increment = direction === 'desc' ? 1 : -1;
  let searchIndex = currentIndex + increment;

  // Search for next non-empty cage (cage with at least one visible animal)
  while (searchIndex >= 0 && searchIndex < collections.length) {
    const candidateCage = collections[searchIndex];
    const animalsInCage = availableSubjects.filter((subject) => subject.cage_id === candidateCage.id);

    if (animalsInCage.length > 0) {
      return candidateCage;
    }

    searchIndex += increment;
  }

  // No more non-empty cages found ahead, return current cage
  return collections[currentIndex];
};

export const reducer: ShowStateReducer = (state, action) => {
  switch (action.type) {
    case 'setCollections': {
      return { ...state, collections: action.data };
    }
    case 'setFilterOptions': {
      return { ...state, filterOptions: action.data };
    }
    case 'updateChecklist':
      return {
        ...state,
        checklist: action.data,
      };
    case 'update':
      return {
        ...state,
        ...action.data,
      };
    default:
      return state;
  }
};

const getFilterOperation = (name) => Object.keys(filterType).find((key) => filterType[key] === name);

export const generateFilterParams = (filters: Array<unknown>, filterMatch: unknown): string => {
  const newFilterParamsObj = new URLSearchParams({
    filterType: filterMatch ? 'and' : 'or',
  });
  filters.forEach((filter) => {
    const isNotMetadata = !String(filter.category.value).includes('metadata.');
    if (isNotMetadata && filter.option.value !== '') {
      updateQueryParams(newFilterParamsObj, {
        'filters[]': `${filter.category.value}|${getFilterOperation(filter.operation.name)}|${filter.option.value}`,
      });
    }
  });
  return newFilterParamsObj.toString();
};

export const unresolvedAlerts = (alerts: Record<string, Record<string, AnimalAlertV1>>) => {
  return Object.values(alerts || {}).flatMap((alertsById) =>
    Object.values(alertsById).filter((alert) => _isNil(alert.resolved_at))
  );
};
